import { useState } from "react";
import { CheckCircle2, MoreHorizontal, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { StatusBadge } from "@/components/common/StatusBadge";
import { ConfirmDialog } from "@/components/common/ConfirmDialog";
import { PaymentFormDialog } from "@/components/finance/FinanceForms";
import { api, errMsg } from "@/lib/api";
import { brl, fmtDate, KINDS, METHODS } from "@/lib/format";

export const PaymentsTable = ({ payments, onChanged, showStudent = true }) => {
  const [editing, setEditing] = useState(null);
  const [removing, setRemoving] = useState(null);

  const pay = async (p, method) => {
    try {
      await api.post(`/payments/${p.id}/pay`, { method });
      toast.success("Pagamento registrado");
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/payments/${removing.id}`);
      toast.success("Pagamento removido");
      onChanged?.();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div className="overflow-x-auto rounded-xl border bg-card">
      <table className="w-full min-w-[720px] text-left text-sm" data-testid="payments-table">
        <thead>
          <tr className="border-b bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
            {showStudent && <th className="p-3">Aluno</th>}
            <th className="p-3">Descrição</th>
            <th className="p-3">Vencimento</th>
            <th className="p-3 text-right">Valor</th>
            <th className="p-3">Status</th>
            <th className="p-3">Pagamento</th>
            <th className="p-3" />
          </tr>
        </thead>
        <tbody>
          {payments.map((p) => (
            <tr key={p.id} className="border-b border-border/50 transition-colors last:border-0 hover:bg-muted/30" data-testid={`payment-row-${p.id}`}>
              {showStudent && <td className="p-3 font-medium">{p.student_name}</td>}
              <td className="p-3">{p.description || KINDS[p.kind]}<span className="block text-xs text-muted-foreground">{KINDS[p.kind]}</span></td>
              <td className="p-3 font-mono">{fmtDate(p.due_date)}</td>
              <td className="p-3 text-right font-mono font-semibold">{brl(p.amount)}</td>
              <td className="p-3"><StatusBadge status={p.status} testId={`payment-status-${p.id}`} /></td>
              <td className="p-3 text-xs text-muted-foreground">{p.paid_date ? `${fmtDate(p.paid_date)} · ${METHODS[p.method] || "—"}` : "—"}</td>
              <td className="p-3 text-right">
                <DropdownMenu>
                  <DropdownMenuTrigger className="rounded-md p-2 hover:bg-muted" data-testid={`payment-actions-${p.id}`}><MoreHorizontal className="h-4 w-4" /></DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    {["pendente", "atrasado"].includes(p.status) && (
                      <>
                        <DropdownMenuLabel className="flex items-center gap-2 text-xs"><CheckCircle2 className="h-3.5 w-3.5" />Marcar como pago via</DropdownMenuLabel>
                        {Object.entries(METHODS).map(([k, v]) => (
                          <DropdownMenuItem key={k} onClick={() => pay(p, k)} data-testid={`payment-pay-${k}-${p.id}`}>{v}</DropdownMenuItem>
                        ))}
                        <DropdownMenuSeparator />
                      </>
                    )}
                    <DropdownMenuItem onClick={() => setEditing(p)} data-testid={`payment-edit-${p.id}`}><Pencil className="mr-2 h-4 w-4" />Editar</DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setRemoving(p)} className="text-destructive" data-testid={`payment-delete-${p.id}`}><Trash2 className="mr-2 h-4 w-4" />Excluir</DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <PaymentFormDialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)} payment={editing} onSaved={onChanged} />
      <ConfirmDialog open={!!removing} onOpenChange={(o) => !o && setRemoving(null)} title="Excluir pagamento?" destructive
        description="Cobranças de mensalidade serão marcadas como canceladas." confirmLabel="Excluir" onConfirm={remove} />
    </div>
  );
};

export const PackageCard = ({ pkg, onCancel }) => {
  const pct = Math.min(100, Math.round((pkg.used / pkg.total_classes) * 100));
  return (
    <div className="rounded-xl border bg-card p-5" data-testid={`package-card-${pkg.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div>
          <p className="font-heading font-semibold">{pkg.name}</p>
          {pkg.student_name && <p className="text-sm text-muted-foreground">{pkg.student_name}</p>}
        </div>
        <StatusBadge status={pkg.status} />
      </div>
      <div className="mt-4 h-2 overflow-hidden rounded-full bg-muted"><div className="h-full rounded-full bg-accent transition-all" style={{ width: `${pct}%` }} /></div>
      <div className="mt-3 grid grid-cols-3 gap-2 text-center text-xs">
        <div><p className="font-mono text-lg font-semibold">{pkg.total_classes}</p>contratadas</div>
        <div><p className="font-mono text-lg font-semibold text-accent" data-testid={`package-used-${pkg.id}`}>{pkg.used}</p>realizadas</div>
        <div><p className="font-mono text-lg font-semibold text-primary" data-testid={`package-remaining-${pkg.id}`}>{pkg.remaining}</p>restantes</div>
      </div>
      <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
        <span>{brl(pkg.price)} · {fmtDate(pkg.purchase_date)}</span>
        {onCancel && pkg.status === "ativo" && <button onClick={() => onCancel(pkg)} className="text-destructive hover:underline" data-testid={`package-cancel-${pkg.id}`}>Cancelar</button>}
      </div>
    </div>
  );
};
